<template>
  <nav class="tf-nav" :class="{ collapsed: isCollapsed }">
    <!-- Toggle Button -->
    <button class="nav-toggle" @click="toggleNav" :title="isCollapsed ? 'Expand' : 'Collapse'">
      <span class="toggle-icon">{{ isCollapsed ? '▶' : '◀' }}</span>
    </button>

    <!-- Navigation Items -->
    <div class="nav-items">
      <router-link
        v-for="item in navItems"
        :key="item.route"
        :to="item.route"
        class="nav-item"
        :class="{ active: isActiveRoute(item.route) }"
        :title="item.label"
      >
        <span v-if="item.iconType === 'image'" class="nav-icon">
          <img :src="item.icon" alt="" class="nav-icon-img" />
        </span>
        <span v-else class="nav-icon">{{ item.icon }}</span>
        <span class="nav-label" v-show="!isCollapsed">{{ item.label }}</span>
      </router-link>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()

// Navigation state
const isCollapsed = ref(true)

// Navigation items
const navItems = [
  {
    icon: '/icons/folder-yellow.svg',
    iconType: 'image',
    label: 'Home',
    route: '/tf',
  },
  {
    icon: '🌐',
    iconType: 'emoji',
    label: 'Public',
    route: '/tf/public',
  },
  {
    icon: '👥',
    iconType: 'emoji',
    label: 'Social',
    route: '/tf/feed',
  },
  {
    icon: '⚙️',
    iconType: 'emoji',
    label: 'Settings',
    route: '/tf/settings',
  },
]

// Load collapsed state from localStorage
onMounted(() => {
  const saved = localStorage.getItem('tf-nav-collapsed')
  if (saved !== null) {
    isCollapsed.value = saved === 'true'
  }
})

// Toggle navigation
const toggleNav = () => {
  isCollapsed.value = !isCollapsed.value
  localStorage.setItem('tf-nav-collapsed', isCollapsed.value.toString())
}

// Check if route is active
const isActiveRoute = (itemRoute: string) => {
  if (itemRoute === '/tf') {
    return route.path === '/tf'
  }
  return route.path.startsWith(itemRoute)
}
</script>

<style lang="scss" scoped>
.tf-nav {
  position: fixed;
  left: 0;
  top: 80px;
  height: calc(100vh - 80px);
  background: #f8f8f8;
  border-right: 1px solid #eee;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 100;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);

  &.collapsed {
    width: 64px;
  }

  &:not(.collapsed) {
    width: 155px;
  }

  @media (max-width: 768px) {
    top: initial;
    bottom: 0;
    height: 80px;
    width: 100vw !important;
    z-index: 100;
    background: #f8f8f8;
    flex-direction: initial;
    overflow: initial;

    .nav-toggle {
      display: none;
    }

    .nav-items {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
    }

    .nav-item {
      padding: 12px;
      border-radius: 0;
      border-left: 1px solid #eee;
      border-right: 1px solid #eee;
      border-top: 1px solid #eee;
      border-bottom: 1px solid #eee;
    }
  }

  // Scrollbar styling
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: 2px;

    &:hover {
      background: var(--color-text-light);
    }
  }
}

// Toggle Button
.nav-toggle {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 40px;
  margin: 8px 20px;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: darkgray;

  &:hover {
    background: var(--color-background-mute);
    border-color: var(--color-primary);
  }
}

.toggle-icon {
  font-size: 12px;
  transition: transform 0.3s;
}

// Navigation Items
.nav-items {
  flex: 1;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  color: var(--color-text);
  position: relative;
  min-height: 48px;

  &:hover {
    background: var(--color-background-mute);
  }

  &.active {
    background: var(--color-primary-light, rgba(66, 133, 244, 0.1));
    color: var(--color-primary);
    font-weight: 500;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 32px;
      background: var(--color-primary);
      border-radius: 0 2px 2px 0;
    }
  }
}

.nav-icon {
  font-size: 20px;
  min-width: 24px;
  text-align: center;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .nav-icon-img {
    width: 23px;
    height: 23px;
    object-fit: contain;
  }
}

.nav-label {
  white-space: nowrap;
  font-size: 14px;
  opacity: 1;
  transition: opacity 0.2s;

  .collapsed & {
    opacity: 0;
    width: 0;
  }
}
</style>
